import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Collapse,
  TableContainer,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { fetchApi } from './fetchApi';

interface LagerplatzZeile {
  lagerort_id: number;
  lagerort_name: string;
  regalnummer: string | null;
  fachnummer: string | null;
  boxnummer: string;
  sku: string;
  bezeichnung: string;
  menge: number;
}

interface LagerortGruppe {
  lagerort_id: number;
  lagerort_name: string;
  zeilen: LagerplatzZeile[];
  gesamtmenge: number;
}

function groupByLagerort(zeilen: LagerplatzZeile[]): LagerortGruppe[] {
  const map = new Map<number, LagerortGruppe>();
  for (const z of zeilen) {
    if (!map.has(z.lagerort_id)) {
      map.set(z.lagerort_id, {
        lagerort_id: z.lagerort_id,
        lagerort_name: z.lagerort_name,
        zeilen: [],
        gesamtmenge: 0,
      });
    }
    const gruppe = map.get(z.lagerort_id)!;
    gruppe.zeilen.push(z);
    gruppe.gesamtmenge += z.menge;
  }
  return Array.from(map.values());
}

function LagerortRow({ gruppe }: { gruppe: LagerortGruppe }) {
  const [offen, setOffen] = useState(false);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
        onClick={() => setOffen((v) => !v)}
      >
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small">
            {offen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{gruppe.lagerort_name}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {gruppe.gesamtmenge} Stk.
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, backgroundColor: '#fafafa' }}>
          <Collapse in={offen} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, px: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Regal</TableCell>
                    <TableCell>Fach</TableCell>
                    <TableCell>Box</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Bezeichnung</TableCell>
                    <TableCell align="right">Menge</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gruppe.zeilen.map((z, i) => (
                    <TableRow key={i}>
                      <TableCell>{z.regalnummer ?? '–'}</TableCell>
                      <TableCell>{z.fachnummer ?? '–'}</TableCell>
                      <TableCell>{z.boxnummer}</TableCell>
                      <TableCell>{z.sku}</TableCell>
                      <TableCell>{z.bezeichnung}</TableCell>
                      <TableCell align="right">{z.menge}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5} sx={{ fontWeight: 600 }}>Summe</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {gruppe.gesamtmenge}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ArtikelProLagerplatzTabelle() {
  const [gruppen, setGruppen] = useState<LagerortGruppe[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  useEffect(() => {
    fetchApi('/api/view/artikel-pro-lagerplatz')
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((rows: LagerplatzZeile[]) => {
        setGruppen(groupByLagerort(Array.isArray(rows) ? rows : []));
      })
      .catch((err: any) => {
        setFehler(
          err?.message === '401'
            ? 'Session abgelaufen. Bitte neu einloggen.'
            : 'Daten konnten nicht geladen werden.'
        );
      })
      .finally(() => setLaedt(false));
  }, []);

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={2}>
        <Typography variant="h5">Artikel pro Lagerplatz</Typography>
      </Box>

      {fehler && <Alert severity="error">{fehler}</Alert>}

      {laedt ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Lagerort</TableCell>
                <TableCell align="right">Gesamtmenge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gruppen.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ opacity: 0.7 }}>
                    Kein Bestand vorhanden.
                  </TableCell>
                </TableRow>
              ) : (
                gruppen.map((g) => <LagerortRow key={g.lagerort_id} gruppe={g} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
